import { Position } from '@xyflow/react';
import dayjs from 'dayjs';
import { nanoid } from 'nanoid';
import { CUSTOM_NODE } from '../const';
import type { Edge, Node } from '../types';
export const createNode = <T extends Node>({
  data,
  position,
  id,
  type,
  ...rest
}: Omit<Node, 'id' | 'data'> & { id?: string; data: Partial<T['data']> }) => {
  return {
    id: id || `${nanoid(16)}`,
    type: type || CUSTOM_NODE,
    data,
    position,
    targetPosition: Position.Left,
    sourcePosition: Position.Right,
    ...rest,
  } as T;
};

export const createEdge = <T extends Edge>({
  source,
  target,
  sourceHandle,
  targetHandle,
  data,
  id,
}: Omit<Edge, 'id' | 'data'> & { id?: string; data: Partial<T['data']> }) => {
  return {
    id: id || `${nanoid(16)}`,
    type: 'custom',
    source,
    target,
    sourceHandle,
    targetHandle,
    data,
  } as T;
};

type ConnectedSourceOrTargetNodesChange = {
  type: 'add' | 'remove';
  edge: Edge;
}[];

/**
 * Given a list of changes (add or remove edges) and all nodes, return a map where
 * the key is the node id and the value is an object with two properties:
 * `_connectedSourceHandleIds` and `_connectedTargetHandleIds`, which are arrays of
 * strings representing the ids of the handles that the node is connected to.
 *
 * This function is used to update the state of the nodes when an edge is added or
 * removed.
 *
 * @param changes - A list of changes. Each change is an object with two properties:
 *   `type` (string, either "add" or "remove") and `edge` (the edge that was added or
 *   removed).
 * @param nodes - A list of all nodes.
 * @returns A map where the key is the node id and the value is an object with two
 *   properties: `_connectedSourceHandleIds` and `_connectedTargetHandleIds`.
 */
export const getNodesConnectedSourceOrTargetHandleIdsMap = (
  changes: ConnectedSourceOrTargetNodesChange,
  nodes: Node[],
) => {
  const nodesConnectedSourceOrTargetHandleIdsMap = {} as Record<string, any>;

  changes.forEach((change) => {
    const { edge, type } = change;
    const sourceNode = nodes.find((node) => node.id === edge.source)!;
    if (sourceNode) {
      nodesConnectedSourceOrTargetHandleIdsMap[sourceNode.id] =
        nodesConnectedSourceOrTargetHandleIdsMap[sourceNode.id] || {
          _connectedSourceHandleIds: [
            ...(sourceNode?.data._connectedSourceHandleIds || []),
          ],
          _connectedTargetHandleIds: [
            ...(sourceNode?.data._connectedTargetHandleIds || []),
          ],
        };
    }

    const targetNode = nodes.find((node) => node.id === edge.target)!;
    if (targetNode) {
      nodesConnectedSourceOrTargetHandleIdsMap[targetNode.id] =
        nodesConnectedSourceOrTargetHandleIdsMap[targetNode.id] || {
          _connectedSourceHandleIds: [
            ...(targetNode?.data._connectedSourceHandleIds || []),
          ],
          _connectedTargetHandleIds: [
            ...(targetNode?.data._connectedTargetHandleIds || []),
          ],
        };
    }

    if (sourceNode) {
      if (type === 'remove') {
        const index = nodesConnectedSourceOrTargetHandleIdsMap[
          sourceNode.id
        ]._connectedSourceHandleIds.findIndex(
          (handleId: string) => handleId === edge.sourceHandle,
        );
        nodesConnectedSourceOrTargetHandleIdsMap[
          sourceNode.id
        ]._connectedSourceHandleIds.splice(index, 1);
      }

      if (type === 'add')
        nodesConnectedSourceOrTargetHandleIdsMap[
          sourceNode.id
        ]._connectedSourceHandleIds.push(edge.sourceHandle || 'source');
    }

    if (targetNode) {
      if (type === 'remove') {
        const index = nodesConnectedSourceOrTargetHandleIdsMap[
          targetNode.id
        ]._connectedTargetHandleIds.findIndex(
          (handleId: string) => handleId === edge.targetHandle,
        );
        nodesConnectedSourceOrTargetHandleIdsMap[
          targetNode.id
        ]._connectedTargetHandleIds.splice(index, 1);
      }

      if (type === 'add')
        nodesConnectedSourceOrTargetHandleIdsMap[
          targetNode.id
        ]._connectedTargetHandleIds.push(edge.targetHandle || 'target');
    }
  });

  return nodesConnectedSourceOrTargetHandleIdsMap;
};

export const formatTimeFromNow = (timestamp: number) => {
  return dayjs(timestamp);
};
